import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { formatBytes, useDeleteMedia, useEpisodeFromMedia, useMediaLibrary, type LibraryFile } from '../lib/media';
import { formatDuration, loudnessVerdict, useShows, useUploadMedia } from '../lib/shows';
import { errorInfo } from '../lib/api';
import { Button } from '../components/ui/Button';
import { ConfirmDialog } from '../components/ui/ConfirmDialog';
import { Dialog } from '../components/ui/Dialog';
import { SelectField } from '../components/ui/Field';
import { Badge, EmptyState, ErrorMessage, Loading, PageHeader } from '../components/ui/Feedback';
import { useToast } from '../components/ui/Toast';
import { AudioDrop } from '../components/shows/bits';
import { mediaUrl } from './EpisodeDetailPage';

export function MediaLibraryPage() {
  const library = useMediaLibrary();
  const upload = useUploadMedia();
  const toast = useToast();
  const [progress, setProgress] = useState<number | null>(null);
  const [query, setQuery] = useState('');
  const [toEpisode, setToEpisode] = useState<LibraryFile | null>(null);
  const [deleting, setDeleting] = useState<LibraryFile | null>(null);

  const onFile = (file: File) => {
    setProgress(0);
    upload.mutate(
      { file, onProgress: setProgress },
      {
        onSuccess: () => toast.show('Uploaded — analysing loudness and length…', 'success'),
        onError: (err) => toast.show(errorInfo(err).message, 'error'),
        onSettled: () => setProgress(null),
      },
    );
  };

  const q = query.trim().toLowerCase();
  const files = (library.data?.media ?? []).filter((m) => !q || (m.originalName ?? '').toLowerCase().includes(q));

  return (
    <div className="space-y-6">
      <PageHeader title="Media" description="Every file you’ve uploaded. Turn one into an episode, or clear out what you don’t use." />
      <AudioDrop onFile={onFile} progress={progress} label="Upload audio" />

      {library.isLoading && <Loading />}
      {library.isError && <ErrorMessage error={library.error} onRetry={() => library.refetch()} />}
      {library.data && library.data.media.length === 0 && (
        <EmptyState title="No files yet" body="Upload a recording above, then make it an episode in one click." />
      )}
      {library.data && library.data.media.length > 0 && (
        <>
          <div className="flex flex-wrap items-center gap-3">
            <input
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Find a file"
              aria-label="Find a file"
              className="w-64 max-w-full rounded-md border border-border bg-bg px-3 py-1.5 text-sm"
            />
            <span className="text-xs text-muted">
              {library.data.total === 1 ? '1 file' : `${library.data.total} files`}
            </span>
          </div>
          {files.length === 0 ? (
            <p className="text-sm text-muted">No files match “{query}”.</p>
          ) : (
            <ul className="divide-y divide-border rounded-xl border border-border">
              {files.map((m) => (
                <FileRow key={m.id} file={m} onEpisode={() => setToEpisode(m)} onDelete={() => setDeleting(m)} />
              ))}
            </ul>
          )}
        </>
      )}

      <NewEpisodeDialog file={toEpisode} onClose={() => setToEpisode(null)} />
      <DeleteDialog file={deleting} onClose={() => setDeleting(null)} />
    </div>
  );
}

function FileRow({ file: m, onEpisode, onDelete }: { file: LibraryFile; onEpisode: () => void; onDelete: () => void }) {
  const [playing, setPlaying] = useState(false);
  const verdict = loudnessVerdict(m.loudnessLufs);
  return (
    <li className="space-y-2 px-4 py-3">
      <div className="flex flex-wrap items-center gap-3">
        <button
          type="button"
          onClick={() => setPlaying((p) => !p)}
          aria-label={`${playing ? 'Hide player for' : 'Play'} ${m.originalName ?? 'file'}`}
          className="flex size-8 shrink-0 items-center justify-center rounded-full bg-accent-soft text-accent hover:bg-accent hover:text-accent-contrast"
        >
          {playing ? '■' : '▶'}
        </button>
        <div className="min-w-0 flex-1">
          <div className="truncate font-medium" title={m.originalName ?? undefined}>
            {m.originalName ?? 'Untitled file'}
          </div>
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted">
            <span>{m.durationMs ? formatDuration(m.durationMs) : 'Analysing…'}</span>
            <span>{formatBytes(m.fileSizeBytes)}</span>
            <span>{new Date(m.createdAt).toLocaleDateString()}</span>
            {verdict && <Badge tone={verdict.ok ? 'success' : 'warning'}>{verdict.label}</Badge>}
            {m.deliveryPath && <span className="text-success">✓ Mastered</span>}
            {m.editedPath && <span>Edited</span>}
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2 text-sm">
          {m.usedBy.length > 0 ? (
            m.usedBy.map((e) => (
              <Link key={e.id} to={`/podcasts/${e.podcastId}/episodes/${e.id}`} className="text-accent hover:underline">
                {e.title}
                {e.status === 'published' ? ' · published' : ''}
              </Link>
            ))
          ) : (
            <>
              <Button size="sm" variant="secondary" onClick={onEpisode}>
                Make an episode
              </Button>
              <Button size="sm" variant="ghost" onClick={onDelete}>
                Delete
              </Button>
            </>
          )}
        </div>
      </div>
      {playing && <audio controls autoPlay preload="metadata" src={mediaUrl(m.storagePath)} className="w-full" />}
    </li>
  );
}

function NewEpisodeDialog({ file, onClose }: { file: LibraryFile | null; onClose: () => void }) {
  const shows = useShows();
  const create = useEpisodeFromMedia();
  const navigate = useNavigate();
  const toast = useToast();
  const list = shows.data?.podcasts ?? [];
  const [podcastId, setPodcastId] = useState('');
  const chosen = podcastId || list[0]?.id || '';

  return (
    <Dialog
      open={!!file}
      onClose={onClose}
      title="Make an episode"
      description={file ? `A draft episode with “${file.originalName ?? 'this file'}” attached.` : undefined}
      actions={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button
            disabled={!chosen}
            loading={create.isPending}
            onClick={() =>
              file &&
              create.mutate(
                { podcastId: chosen, file },
                {
                  onSuccess: (ep) => {
                    onClose();
                    navigate(`/podcasts/${ep.podcastId}/episodes/${ep.id}`);
                  },
                  onError: (err) => toast.show(errorInfo(err).message, 'error'),
                },
              )
            }
          >
            Create episode
          </Button>
        </>
      }
    >
      {shows.isLoading && <Loading />}
      {shows.data && list.length === 0 && (
        <p className="text-sm text-muted">
          You don’t have a show yet. <Link to="/podcasts" className="text-accent underline">Create one</Link> first.
        </p>
      )}
      {list.length > 0 && (
        <SelectField
          label="Show"
          value={chosen}
          onChange={(e) => setPodcastId(e.target.value)}
          options={list.map((s) => ({ value: s.id, label: s.title }))}
        />
      )}
    </Dialog>
  );
}

function DeleteDialog({ file, onClose }: { file: LibraryFile | null; onClose: () => void }) {
  const remove = useDeleteMedia();
  const toast = useToast();
  return (
    <ConfirmDialog
      open={!!file}
      danger
      title={`Delete “${file?.originalName ?? 'this file'}”?`}
      body="The original, its mastered MP3 and any edited version are removed. This can’t be undone."
      confirmLabel="Delete file"
      busy={remove.isPending}
      onCancel={onClose}
      onConfirm={() =>
        file &&
        remove.mutate(file.id, {
          onSuccess: () => {
            toast.show('File deleted', 'success');
            onClose();
          },
          onError: (err) => {
            toast.show(
              errorInfo(err).code === 'media_in_use'
                ? 'An episode uses this file. Replace its audio first.'
                : errorInfo(err).message,
              'error',
            );
            onClose();
          },
        })
      }
    />
  );
}
